import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const empty = { name: "", email: "", phone: "", role: "waiter", shift: "morning", salary: 2500 };
const ROLES = ["manager", "cashier", "kitchen", "waiter"];
const SHIFTS = ["morning", "afternoon", "evening", "night"];

export default function Employees() {
  const [list, setList] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/employees"), api.get("/attendance")]);
    setList(a.data); setAttendance(b.data);
  };
  useEffect(() => { load(); }, []);

  const att = (id) => attendance.find((a) => a.employee_id === id)?.status;

  const openCreate = () => { setEdit(null); setForm(empty); setOpen(true); };
  const openEdit = (e) => {
    setEdit(e);
    setForm({ name: e.name, email: e.email || "", phone: e.phone || "", role: e.role, shift: e.shift, salary: e.salary });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const payload = {
      ...form,
      name: form.name.trim(),
      phone: form.phone.trim() || null,
      email: form.email.trim() || null,
    };
    try {
      if (edit) await api.put(`/employees/${edit.id}`, payload);
      else await api.post("/employees", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail ? String(Array.isArray(e.response.data.detail) ? e.response.data.detail[0]?.msg : e.response.data.detail) : "Failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete employee?")) return;
    try { await api.delete(`/employees/${id}`); load(); } catch { toast.error("Forbidden or failed"); }
  };
  const mark = async (id, status) => {
    try {
      await api.post(`/attendance`, { employee_id: id, status });
      toast.success("Marked");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Team"
        title="Employees"
        subtitle="Manage staff roster, shifts, salary and daily attendance."
        actions={<Button onClick={openCreate} className="btn-primary" data-testid="emp-add-btn"><Plus size={16} weight="bold"/> Add employee</Button>}
      />

      <div className="bento overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 label-eyebrow border-b border-[#EAE7E0] bg-[var(--bg-secondary)]/50">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Shift</div>
          <div className="col-span-2">Salary</div>
          <div className="col-span-2">Today</div>
          <div className="col-span-1 text-right"></div>
        </div>
        <div className="divide-y divide-[#EAE7E0]">
          {list.map((e) => (
            <div key={e.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center" data-testid={`emp-row-${e.id}`}>
              <div className="col-span-3">
                <div className="font-semibold text-sm">{e.name}</div>
                <div className="text-[11px] text-[var(--text-secondary)]">{e.email}</div>
              </div>
              <div className="col-span-2 capitalize text-sm font-semibold">{e.role}</div>
              <div className="col-span-2 capitalize text-sm">{e.shift}</div>
              <div className="col-span-2 text-sm font-mono">{fmtMoney(e.salary)}</div>
              <div className="col-span-2 flex gap-1">
                {["present", "absent", "leave"].map((s) => (
                  <button
                    key={s}
                    onClick={() => mark(e.id, s)}
                    className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold ${att(e.id) === s ? (s === "present" ? "bg-[var(--brand-2)] text-white" : s === "absent" ? "bg-[var(--brand)] text-white" : "bg-[var(--status-served)] text-white") : "bg-[var(--bg-secondary)]"}`}
                    data-testid={`emp-att-${e.id}-${s}`}
                  >{s[0].toUpperCase()}</button>
                ))}
              </div>
              <div className="col-span-1 flex justify-end gap-1">
                <button onClick={() => openEdit(e)} className="p-1.5 rounded hover:bg-[var(--bg-secondary)]" data-testid={`emp-edit-${e.id}`}><PencilSimple size={14}/></button>
                <button onClick={() => del(e.id)} className="p-1.5 rounded text-[var(--brand)] hover:bg-[var(--bg-secondary)]" data-testid={`emp-del-${e.id}`}><Trash size={14}/></button>
              </div>
            </div>
          ))}
          {list.length === 0 && <div className="px-4 py-8 text-center text-sm text-[var(--text-secondary)]">No employees yet.</div>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="emp-dialog">
          <DialogHeader><DialogTitle>{edit ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="emp-form-name"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="emp-form-email"/>
              </div>
              <div>
                <Label className="label-eyebrow">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="emp-form-phone"/>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="label-eyebrow">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="emp-form-role"><SelectValue/></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Shift</Label>
                <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                  <SelectTrigger className="mt-1" data-testid="emp-form-shift"><SelectValue/></SelectTrigger>
                  <SelectContent>{SHIFTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Salary</Label>
                <Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: parseFloat(e.target.value || 0) })} className="mt-1" data-testid="emp-form-salary"/>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="btn-primary" data-testid="emp-form-save">{edit ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
